import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Goal, Milestone, JourneyEntry } from '../types';
import { getMilestonesByGoal, updateMilestone } from '../firebase/milestones.service';
import { 
  getJourneyEntriesByGoal, 
  createReflection,
  createJourneyStart,
  deleteJourneyEntry,
} from '../firebase/journey.service';
import {
  logVideoRecorded,
  logVideoRerecorded,
  logVideoDeleted,
} from '../firebase/changelog.service';
import MilestoneEditModal from './MilestoneEditModal';
import VideoSlotModal from './VideoSlotModal';
import { CalendarIcon, GoalIcon, MilestoneIcon, GrowthIcon, VideoIcon, InfoIcon, ClipboardIcon, TrendIcon, AnalyticsIcon, WarningIcon } from '../../../icons';

interface GoalTimelineProps {
  goal: Goal;
  ownerId: string;
  milestones?: Milestone[];
  onMilestoneUpdated?: () => void;
  refreshKey?: number; // Increment to trigger journey data reload
}

type ViewMode = 'week' | 'month' | 'year' | 'allYears';

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  week: 'Week',
  month: 'Maand',
  year: 'Jaar',
  allYears: 'Jaren',
};

// Day names in Dutch (Monday first)
const DAY_NAMES_SHORT = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

// Fixed widths for clear visibility
const DAY_WIDTH = 36; // pixels per day
const YEAR_BLOCK_WIDTH = 120; // width for year blocks in year view (larger, focused on years)
const MONTH_IN_YEAR_WIDTH = 50; // width for months in year view (only shown if milestone present)

/**
 * DatePickerModal - Modal for picking exact date after drag-drop
 */
interface DatePickerModalProps {
  milestone: Milestone;
  targetYear: number;
  targetMonth: number;
  goalStartDate: Date;
  goalEndDate: Date;
  onSave: (date: Date) => void;
  onClose: () => void;
}

const DatePickerModal: React.FC<DatePickerModalProps> = ({
  milestone,
  targetYear,
  targetMonth,
  goalStartDate,
  goalEndDate,
  onSave,
  onClose,
}) => {
  const [selectedDay, setSelectedDay] = useState(15); // Default to middle of month
  
  const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  
  // Calculate valid range within this month
  const monthStart = new Date(targetYear, targetMonth, 1);
  const monthEnd = new Date(targetYear, targetMonth, daysInMonth);
  const validStart = monthStart < goalStartDate ? goalStartDate.getDate() : 1;
  const validEnd = monthEnd > goalEndDate ? goalEndDate.getDate() : daysInMonth;
  
  // Generate days for the month
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const firstDayOfWeek = new Date(targetYear, targetMonth, 1).getDay();
  const paddingDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday start
  
  const handleSave = () => {
    const newDate = new Date(targetYear, targetMonth, selectedDay);
    onSave(newDate);
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CalendarIcon size={18} className="text-gray-700" /> Kies datum
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            ✕
          </button>
        </div>
        
        {/* Milestone info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 mb-1">Mijlpaal verplaatsen naar:</div>
          <div className="font-medium text-gray-700">{MONTH_NAMES[targetMonth]} {targetYear}</div>
          <div className="text-sm text-gray-600 mt-1 truncate">{milestone.title}</div>
        </div>
        
        {/* Calendar header */}
        <div className="text-center font-semibold text-gray-700 mb-2">
          {MONTH_NAMES[targetMonth]} {targetYear}
        </div>
        
        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
            <div key={day} className="text-center text-xs text-gray-500 font-medium py-1">
              {day}
            </div>
          ))}
        </div>
        
        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Padding for first week */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`pad-${i}`} className="p-2" />
          ))}
          
          {/* Days */}
          {days.map(day => {
            const isValidDay = day >= validStart && day <= validEnd;
            const isSelected = day === selectedDay;
            
            return (
              <button
                key={day}
                onClick={() => isValidDay && setSelectedDay(day)}
                disabled={!isValidDay}
                className={`
                  p-2 text-sm rounded-lg transition-all
                  ${isSelected 
                    ? 'bg-gray-600 text-white font-bold' 
                    : isValidDay
                      ? 'hover:bg-gray-100 text-gray-700'
                      : 'text-gray-300 cursor-not-allowed'
                  }
                `}
              >
                {day}
              </button>
            );
          })}
        </div>
        
        {/* Selected date preview */}
        <div className="mt-4 p-2 bg-gray-100 rounded-lg text-center">
          <span className="text-sm text-gray-600">Nieuwe datum: </span>
          <span className="font-medium text-gray-800">
            {selectedDay} {MONTH_NAMES[targetMonth]} {targetYear}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Opslaan
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * GoalTimeline - Tijdlijn met altijd zichtbare detail-rij
 */
const GoalTimeline: React.FC<GoalTimelineProps> = ({
  goal,
  ownerId,
  milestones: propMilestones,
  onMilestoneUpdated,
  refreshKey = 0,
}) => {
  const [loadedMilestones, setLoadedMilestones] = useState<Milestone[]>([]);
  
  // Persist viewMode in localStorage to survive re-renders
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(`timeline-viewMode-${goal.id}`);
    return (saved as ViewMode) || 'month';
  });
  
  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    localStorage.setItem(`timeline-viewMode-${goal.id}`, mode);
  };
  
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [isLoading, setIsLoading] = useState(!propMilestones);
  
  // Persist expanded state in localStorage
  const [isExpanded, setIsExpandedState] = useState(() => {
    const saved = localStorage.getItem(`timeline-expanded-${goal.id}`);
    return saved === 'true';
  });
  
  const setIsExpanded = (expanded: boolean) => {
    setIsExpandedState(expanded);
    localStorage.setItem(`timeline-expanded-${goal.id}`, expanded.toString());
  };
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const expandedScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Journey state
  const [journeyEntries, setJourneyEntries] = useState<JourneyEntry[]>([]);
  const [selectedVideoSlot, setSelectedVideoSlot] = useState<{
    date: Date;
    dayNumber: number;
    isAvailable: boolean;
    isRecorded: boolean;
    entry?: JourneyEntry;
  } | null>(null);
  
  // Drag and drop state for allYears view
  const [draggedMilestone, setDraggedMilestone] = useState<Milestone | null>(null);
  const [dropTarget, setDropTarget] = useState<{ year: number; month: number } | null>(null);
  const [showDatePickerModal, setShowDatePickerModal] = useState<{
    milestone: Milestone;
    targetYear: number;
    targetMonth: number;
  } | null>(null);

  const milestones = propMilestones || loadedMilestones;

  // Load milestones
  useEffect(() => {
    if (propMilestones) {
      setIsLoading(false);
      return;
    }
    
    const loadMilestones = async () => {
      setIsLoading(true);
      try {
        const data = await getMilestonesByGoal(ownerId, goal.id);
        setLoadedMilestones(data);
      } catch (err) {
        console.error('Failed to load milestones:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadMilestones();
  }, [goal.id, ownerId, propMilestones]);

  // Load journey entries (refreshKey triggers reload when journey start is created)
  useEffect(() => {
    const loadJourneyData = async () => {
      try {
        const entries = await getJourneyEntriesByGoal(ownerId, goal.id);
        setJourneyEntries(entries);
      } catch (err) {
        console.error('Failed to load journey data:', err);
      }
    };
    loadJourneyData();
  }, [goal.id, ownerId, refreshKey]);

  // Parse goal dates
  const goalStartDate = useMemo(() => {
    const d = new Date(goal.startDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [goal.startDate]);
  
  const goalEndDate = useMemo(() => {
    const d = new Date(goal.endDate);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [goal.endDate]);
  
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  /**
   * Get Monday of the week containing a date
   */
  const getMondayOfWeek = (date: Date): Date => {
    const d = new Date(date);
    const dayOfWeek = d.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + daysToMonday);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  /**
   * Generate timeline data based on view mode
   */
  // Get milestones with dates for determining which years have milestones
  const milestonesWithDates = useMemo(() => {
    return milestones.map(m => ({
      milestone: m,
      date: calculateMilestoneDate(m, goalStartDate, goalEndDate),
    })).filter(m => m.date !== null);
  }, [milestones, goalStartDate, goalEndDate]);

  const timelineData = useMemo(() => {
    if (viewMode === 'allYears') {
      // ALL YEARS VIEW: Each year as a row with monthly timeline
      // Allows dragging milestones between years/months
      const startYear = goalStartDate.getFullYear();
      const endYear = goalEndDate.getFullYear();
      const totalYears = endYear - startYear + 1;
      
      const yearRows: {
        year: number;
        isCurrent: boolean;
        isPast: boolean;
        months: {
          month: number;
          label: string;
          isCurrent: boolean;
          isPast: boolean;
          hasMilestones: boolean;
          milestoneCount: number;
          milestonesInMonth: typeof milestonesWithDates;
        }[];
      }[] = [];
      
      for (let year = startYear; year <= endYear; year++) {
        const isCurrent = today.getFullYear() === year;
        const isPast = year < today.getFullYear();
        
        const months: typeof yearRows[0]['months'] = [];
        
        for (let month = 0; month < 12; month++) {
          const monthIsCurrent = isCurrent && today.getMonth() === month;
          const monthIsPast = isPast || (isCurrent && today.getMonth() > month);
          
          // Find milestones in this month
          const milestonesInMonth = milestonesWithDates.filter(m => 
            m.date && m.date.getFullYear() === year && m.date.getMonth() === month
          );
          
          months.push({
            month,
            label: MONTH_NAMES[month],
            isCurrent: monthIsCurrent,
            isPast: monthIsPast,
            hasMilestones: milestonesInMonth.length > 0,
            milestoneCount: milestonesInMonth.length,
            milestonesInMonth,
          });
        }
        
        yearRows.push({
          year,
          isCurrent,
          isPast,
          months,
        });
      }
      
      return {
        type: 'allYears' as const,
        yearRows,
        totalYears,
        totalWidth: 0, // Not used for this view
        getMilestonePosition: () => 0, // Not used for this view
        segmentWidth: 0,
      };
    } else if (viewMode === 'year') {
      // YEAR VIEW: Focus on YEARS as the main blocks
      // Shows the path of years you need to travel to reach your goal
      // Only shows month detail if there's a milestone in that year
      const startYear = goalStartDate.getFullYear();
      const endYear = goalEndDate.getFullYear();
      const totalYears = endYear - startYear + 1;
      
      const years: {
        year: number;
        label: string;
        pixelStart: number;
        pixelEnd: number;
        isCurrent: boolean;
        isPast: boolean;
        isFuture: boolean;
        hasMilestones: boolean;
        milestoneMonths: number[]; // Which months have milestones
      }[] = [];

      let pixelOffset = 0;
      
      for (let year = startYear; year <= endYear; year++) {
        const isCurrent = today.getFullYear() === year;
        const isPast = year < today.getFullYear();
        const isFuture = year > today.getFullYear();
        
        // Check if this year has any milestones
        const milestonesInYear = milestonesWithDates.filter(m => 
          m.date && m.date.getFullYear() === year
        );
        const hasMilestones = milestonesInYear.length > 0;
        const milestoneMonths = Array.from(new Set(milestonesInYear.map(m => m.date!.getMonth())));
        
        years.push({
          year,
          label: year.toString(),
          pixelStart: pixelOffset,
          pixelEnd: pixelOffset + YEAR_BLOCK_WIDTH,
          isCurrent,
          isPast,
          isFuture,
          hasMilestones,
          milestoneMonths,
        });

        pixelOffset += YEAR_BLOCK_WIDTH;
      }

      const totalWidth = pixelOffset;
      
      // Calculate milestone positions for year view - spread within year block
      const getMilestonePosition = (date: Date): number => {
        const year = date.getFullYear();
        const yearData = years.find(y => y.year === year);
        
        if (!yearData) {
          if (date < goalStartDate) return 0;
          return totalWidth;
        }
        
        // Position within the year (0-1) based on day of year
        const startOfYear = new Date(year, 0, 1);
        const endOfYear = new Date(year, 11, 31);
        const dayOfYear = Math.floor((date.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
        const totalDaysInYear = Math.floor((endOfYear.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const positionInYear = dayOfYear / totalDaysInYear;
        
        return yearData.pixelStart + (positionInYear * YEAR_BLOCK_WIDTH);
      };

      return { 
        type: 'year' as const, 
        years, 
        totalWidth,
        totalYears,
        getMilestonePosition,
        segmentWidth: YEAR_BLOCK_WIDTH,
      };
    } else if (viewMode === 'month') {
      // MONTH VIEW: Segments for months, days below
      // Start from the 1st of the month containing goalStartDate
      const segments: {
        label: string;
        month: number;
        year: number;
        pixelStart: number;
        pixelEnd: number;
        isCurrent: boolean;
        isPartialStart: boolean; // First month may have grayed out days
        isPartialEnd: boolean;   // Last month may have grayed out days
        days: {
          dayOfMonth: number;
          dayOfWeek: number;
          label: string;
          pixelStart: number;
          pixelEnd: number;
          isCurrent: boolean;
          isBeforeStart: boolean; // Before goalStartDate
          isAfterEnd: boolean;    // After goalEndDate
        }[];
      }[] = [];

      let pixelOffset = 0; // No padding - start from beginning of month
      let current = new Date(goalStartDate.getFullYear(), goalStartDate.getMonth(), 1);

      while (current <= goalEndDate) {
        const year = current.getFullYear();
        const month = current.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const monthWidth = daysInMonth * DAY_WIDTH;
        const isCurrent = today.getFullYear() === year && today.getMonth() === month;
        const isFirstMonth = segments.length === 0;
        const isLastMonth = new Date(year, month + 1, 1) > goalEndDate;

        const days: typeof segments[0]['days'] = [];
        for (let d = 1; d <= daysInMonth; d++) {
          const dayDate = new Date(year, month, d);
          const dayOfWeek = dayDate.getDay();
          const dayIsCurrent = dayDate.toDateString() === today.toDateString();
          const isBeforeStart = dayDate < goalStartDate;
          const isAfterEnd = dayDate > goalEndDate;
          
          days.push({
            dayOfMonth: d,
            dayOfWeek,
            label: d.toString(),
            pixelStart: pixelOffset + ((d - 1) * DAY_WIDTH),
            pixelEnd: pixelOffset + (d * DAY_WIDTH),
            isCurrent: dayIsCurrent,
            isBeforeStart,
            isAfterEnd,
          });
        }

        const showYear = month === 0 || segments.length === 0;
        segments.push({
          label: showYear 
            ? `${MONTH_NAMES[month]} '${year.toString().slice(-2)}`
            : MONTH_NAMES[month],
          month,
          year,
          pixelStart: pixelOffset,
          pixelEnd: pixelOffset + monthWidth,
          isCurrent,
          isPartialStart: isFirstMonth && goalStartDate.getDate() > 1,
          isPartialEnd: isLastMonth && goalEndDate.getDate() < daysInMonth,
          days,
        });

        pixelOffset += monthWidth;
        current = new Date(year, month + 1, 1);
      }

      const totalWidth = pixelOffset;
      
      const getMilestonePosition = (date: Date): number => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const dayOfMonth = date.getDate();
        
        const segment = segments.find(s => s.year === year && s.month === month);
        if (!segment) {
          if (date < goalStartDate) return 0;
          return totalWidth;
        }
        
        const dayData = segment.days.find(d => d.dayOfMonth === dayOfMonth);
        if (!dayData) return segment.pixelStart;
        
        return (dayData.pixelStart + dayData.pixelEnd) / 2;
      };

      return { 
        type: 'month' as const, 
        segments, 
        totalWidth, 
        getMilestonePosition,
        segmentWidth: 30 * DAY_WIDTH, // Average month
      };
    } else {
      // WEEK VIEW: Segments for weeks, days below
      // Start from the Monday of the week containing goalStartDate
      const segments: {
        label: string;
        weekNum: number;
        year: number;
        pixelStart: number;
        pixelEnd: number;
        isCurrent: boolean;
        days: {
          date: Date;
          dayOfMonth: number;
          dayOfWeek: number;
          dayName: string;
          pixelStart: number;
          pixelEnd: number;
          isCurrent: boolean;
          isBeforeStart: boolean;
          isAfterEnd: boolean;
        }[];
      }[] = [];

      let pixelOffset = 0; // No padding - start from Monday of first week
      let weekStart = getMondayOfWeek(goalStartDate);
      const weekWidth = 7 * DAY_WIDTH;

      while (weekStart <= goalEndDate) {
        const weekNum = getWeekNumber(weekStart);
        const year = weekStart.getFullYear();
        const isCurrent = today >= weekStart && today < new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

        const days: typeof segments[0]['days'] = [];
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(weekStart);
          dayDate.setDate(weekStart.getDate() + i);
          const dayIsCurrent = dayDate.toDateString() === today.toDateString();
          const isBeforeStart = dayDate < goalStartDate;
          const isAfterEnd = dayDate > goalEndDate;
          
          days.push({
            date: new Date(dayDate),
            dayOfMonth: dayDate.getDate(),
            dayOfWeek: dayDate.getDay(),
            dayName: DAY_NAMES_SHORT[i],
            pixelStart: pixelOffset + (i * DAY_WIDTH),
            pixelEnd: pixelOffset + ((i + 1) * DAY_WIDTH),
            isCurrent: dayIsCurrent,
            isBeforeStart,
            isAfterEnd,
          });
        }

        segments.push({
          label: `W${weekNum}`,
          weekNum,
          year,
          pixelStart: pixelOffset,
          pixelEnd: pixelOffset + weekWidth,
          isCurrent,
          days,
        });

        pixelOffset += weekWidth;
        weekStart = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      }

      const totalWidth = pixelOffset;
      
      const getMilestonePosition = (date: Date): number => {
        for (const segment of segments) {
          for (const day of segment.days) {
            if (day.date.toDateString() === date.toDateString()) {
              return (day.pixelStart + day.pixelEnd) / 2;
            }
          }
        }
        if (date < goalStartDate) return 0;
        return totalWidth;
      };

      return { 
        type: 'week' as const, 
        segments, 
        totalWidth, 
        getMilestonePosition,
        segmentWidth: weekWidth,
      };
    }
  }, [goalStartDate, goalEndDate, viewMode, today]);

  /**
   * Calculate milestone positions
   */
  const milestonePositions = useMemo(() => {
    const enriched = milestones.map((m, index) => {
      const milestoneDate = calculateMilestoneDate(m, goalStartDate, goalEndDate);
      let pixelPosition: number;
      
      if (milestoneDate) {
        pixelPosition = timelineData.getMilestonePosition(milestoneDate);
      } else {
        pixelPosition = ((index + 1) / (milestones.length + 1)) * timelineData.totalWidth;
      }

      return {
        milestone: m,
        pixelPosition,
        date: milestoneDate,
        isPast: milestoneDate ? milestoneDate < today : false,
        hasDate: !!milestoneDate,
        order: index + 1,
      };
    });

    return enriched.sort((a, b) => a.pixelPosition - b.pixelPosition);
  }, [milestones, timelineData, goalStartDate, goalEndDate, today]);

  // Today marker position
  const todayPixelPosition = useMemo(() => {
    return timelineData.getMilestonePosition(today);
  }, [timelineData, today]);

  // Start marker position
  const startPixelPosition = useMemo(() => {
    return timelineData.getMilestonePosition(goalStartDate);
  }, [timelineData, goalStartDate]);

  // End marker position
  const endPixelPosition = useMemo(() => {
    return timelineData.getMilestonePosition(goalEndDate);
  }, [timelineData, goalEndDate]);

  // Check if today is within goal range
  const isTodayInRange = today >= goalStartDate && today <= goalEndDate;

  // Calculate video slot positions (every 7 days from start)
  const videoSlotPositions = useMemo(() => {
    const slots: {
      date: Date;
      pixelPosition: number;
      dayNumber: number; // 0 = start, 7, 14, 21, etc.
      isAvailable: boolean; // Can record now
      isRecorded: boolean; // Has entry
      entry?: JourneyEntry; // The journey entry if exists
    }[] = [];

    // Start slot (day 0) - always available (commitment video can be recorded before goal starts)
    const startEntry = journeyEntries.find(e => e.type === 'start');
    slots.push({
      date: new Date(goalStartDate),
      pixelPosition: timelineData.getMilestonePosition(goalStartDate),
      dayNumber: 0,
      isAvailable: !startEntry, // Start video is always available (no date restriction)
      isRecorded: !!startEntry,
      entry: startEntry,
    });

    // Weekly slots (every 7 days)
    let slotDate = new Date(goalStartDate);
    slotDate.setDate(slotDate.getDate() + 7);
    let dayNumber = 7;

    while (slotDate <= goalEndDate) {
      const slotDateStart = new Date(slotDate);
      slotDateStart.setHours(0, 0, 0, 0);
      
      // Find matching entry (within same day)
      const matchingEntry = journeyEntries.find(e => {
        if (e.type !== 'reflection') return false;
        const entryDate = new Date(e.createdAt);
        // Check if entry was created within 7 days after this slot opened
        const slotEnd = new Date(slotDateStart);
        slotEnd.setDate(slotEnd.getDate() + 7);
        return entryDate >= slotDateStart && entryDate < slotEnd;
      });

      // Slot is available if: today >= slot date AND no matching entry AND previous slot is filled
      const isAvailable = today >= slotDateStart && !matchingEntry;
      
      slots.push({
        date: new Date(slotDateStart),
        pixelPosition: timelineData.getMilestonePosition(slotDateStart),
        dayNumber,
        isAvailable,
        isRecorded: !!matchingEntry,
        entry: matchingEntry,
      });

      slotDate.setDate(slotDate.getDate() + 7);
      dayNumber += 7;
    }

    return slots;
  }, [goalStartDate, goalEndDate, today, journeyEntries, timelineData]);

  // Auto-scroll to today (or start date if today is before goal)
  useEffect(() => {
    if (!scrollContainerRef.current || isLoading) return;
    
    // Small delay to ensure content is rendered
    const timer = setTimeout(() => {
      if (!scrollContainerRef.current) return;
      
      const container = scrollContainerRef.current;
      const containerWidth = container.clientWidth;
      
      // Determine scroll position - today if in range, otherwise start date
      let scrollTarget: number;
      if (isTodayInRange) {
        // Scroll to today, centered in view
        scrollTarget = todayPixelPosition - (containerWidth / 2);
      } else if (today < goalStartDate) {
        // Today is before goal start - scroll to start (with some padding)
        const startPosition = timelineData.getMilestonePosition(goalStartDate);
        scrollTarget = startPosition - 50; // 50px padding
      } else {
        // Today is after goal end - scroll to end
        scrollTarget = timelineData.totalWidth - containerWidth;
      }
      
      container.scrollTo({ 
        left: Math.max(0, scrollTarget), 
        behavior: 'smooth' 
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isLoading, isTodayInRange, todayPixelPosition, timelineData, today, goalStartDate]);

  /**
   * Navigate to previous/next segment
   */
  const navigateSegment = (direction: 'prev' | 'next') => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const currentScroll = container.scrollLeft;
    const segmentWidth = timelineData.segmentWidth;
    
    if (direction === 'prev') {
      const newScroll = Math.max(0, currentScroll - segmentWidth);
      container.scrollTo({ left: newScroll, behavior: 'smooth' });
    } else {
      const newScroll = Math.min(
        container.scrollWidth - container.clientWidth,
        currentScroll + segmentWidth
      );
      container.scrollTo({ left: newScroll, behavior: 'smooth' });
    }
  };

  /**
   * Navigate to previous/next milestone
   */
  const navigateToMilestone = (direction: 'prev' | 'next') => {
    if (!scrollContainerRef.current || milestonePositions.length === 0) return;
    
    const container = scrollContainerRef.current;
    const currentScroll = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const currentCenter = currentScroll + containerWidth / 2;
    
    // Find milestones based on direction
    const sortedPositions = [...milestonePositions].sort((a, b) => a.pixelPosition - b.pixelPosition);
    
    let targetMilestone;
    if (direction === 'next') {
      targetMilestone = sortedPositions.find(m => m.pixelPosition > currentCenter + 50);
    } else {
      targetMilestone = [...sortedPositions].reverse().find(m => m.pixelPosition < currentCenter - 50);
    }
    
    if (targetMilestone) {
      const newScroll = targetMilestone.pixelPosition - containerWidth / 2;
      container.scrollTo({ 
        left: Math.max(0, Math.min(newScroll, container.scrollWidth - containerWidth)), 
        behavior: 'smooth' 
      });
    }
  };

  /**
   * Get current visible date/period based on scroll position
   */
  const getCurrentVisiblePeriod = (): string => {
    if (!scrollContainerRef.current) return '';
    
    const container = scrollContainerRef.current;
    const currentScroll = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const centerPosition = currentScroll + containerWidth / 2;
    
    if (timelineData.type === 'month') {
      const segment = timelineData.segments.find(s => 
        centerPosition >= s.pixelStart && centerPosition < s.pixelEnd
      );
      if (segment) {
        return `${MONTH_NAMES[segment.month]} ${segment.year}`;
      }
    } else if (timelineData.type === 'week') {
      const segment = timelineData.segments.find(s => 
        centerPosition >= s.pixelStart && centerPosition < s.pixelEnd
      );
      if (segment) {
        // Get the middle day of the week
        const midDay = segment.days[3];
        if (midDay) {
          return `${MONTH_NAMES[midDay.date.getMonth()]} ${midDay.date.getFullYear()}`;
        }
      }
    } else if (timelineData.type === 'year') {
      const year = timelineData.years.find(y => 
        centerPosition >= y.pixelStart && centerPosition < y.pixelEnd
      );
      if (year) {
        return `${year.year}`;
      }
    } else if (timelineData.type === 'allYears') {
      // All years view shows all years stacked, no scroll-based period
      return `${timelineData.yearRows[0]?.year || ''} - ${timelineData.yearRows[timelineData.yearRows.length - 1]?.year || ''}`;
    }
    
    return '';
  };

  // Track current visible period
  const [currentPeriod, setCurrentPeriod] = useState('');
  
  // Update current period on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      setCurrentPeriod(getCurrentVisiblePeriod());
    };
    
    // Initial update
    setTimeout(handleScroll, 200);
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [timelineData]);

  const handleMilestoneClick = (milestone: Milestone) => {
    setSelectedMilestone(milestone);
  };

  const handleMilestoneSave = async (updated: Partial<Milestone>) => {
    if (!selectedMilestone) return;
    
    try {
      await updateMilestone(selectedMilestone.id, updated);
      const data = await getMilestonesByGoal(ownerId, goal.id);
      setLoadedMilestones(data);
      setSelectedMilestone(null);
      
      if (onMilestoneUpdated) {
        onMilestoneUpdated();
      }
    } catch (err) {
      console.error('Failed to update milestone:', err);
    }
  };

  // Journey handlers
  const handleVideoSlotClick = (slot: typeof videoSlotPositions[0]) => {
    setSelectedVideoSlot(slot);
  };

  const handleSaveReflection = async (text: string, videoUrl?: string) => {
    if (!selectedVideoSlot) return;
    
    try {
      const isRerecord = selectedVideoSlot.isRecorded;
      const videoType = selectedVideoSlot.dayNumber === 0 ? 'start' : 'reflection';
      
      if (selectedVideoSlot.dayNumber === 0) {
        // Start entry - pass videoUrl
        await createJourneyStart(ownerId, goal.id, videoUrl);
      } else {
        // Regular reflection - pass text and videoUrl
        await createReflection(ownerId, goal.id, text, videoUrl);
      }
      
      // Log video action
      if (isRerecord) {
        // Count re-records for this slot
        const previousRerecords = journeyEntries.filter(
          e => e.type === videoType && 
          (videoType === 'start' || 
            Math.floor((new Date(e.createdAt).getTime() - goalStartDate.getTime()) / (1000 * 60 * 60 * 24 * 7)) === 
            Math.floor(selectedVideoSlot.dayNumber / 7))
        ).length;
        await logVideoRerecorded(ownerId, goal.id, videoType, selectedVideoSlot.dayNumber, previousRerecords + 1);
      } else {
        await logVideoRecorded(ownerId, goal.id, videoType, selectedVideoSlot.dayNumber);
      }
      
      // Reload journey data
      const entries = await getJourneyEntriesByGoal(ownerId, goal.id);
      setJourneyEntries(entries);
      setSelectedVideoSlot(null);
    } catch (err) {
      console.error('Failed to save reflection:', err);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!selectedVideoSlot) return;
    
    try {
      await deleteJourneyEntry(entryId);
      
      // Log video deletion
      const videoType = selectedVideoSlot.dayNumber === 0 ? 'start' : 'reflection';
      await logVideoDeleted(ownerId, goal.id, videoType, selectedVideoSlot.dayNumber);
      
      setJourneyEntries(prev => prev.filter(e => e.id !== entryId));
      setSelectedVideoSlot(null);
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-center text-gray-400 text-sm py-4">
          Tijdlijn laden...
        </div>
      </div>
    );
  }

  // Header component
  const timelineHeader = (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className={`font-medium text-gray-900 ${isExpanded ? 'text-lg' : 'text-sm'}`}>
          Tijdlijn
        </h3>
        <p className={`text-gray-500 ${isExpanded ? 'text-sm' : 'text-xs'}`}>
          {formatDate(goalStartDate)} → {formatDate(goalEndDate)}
        </p>
      </div>
      
      <div className="flex items-center gap-2">
        {/* View Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {(['week', 'month', 'year', 'allYears'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`
                px-3 py-1 font-medium rounded-md transition-colors
                ${isExpanded ? 'text-sm' : 'text-xs'}
                ${viewMode === mode
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {VIEW_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
        
        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title={isExpanded ? 'Verkleinen' : 'Vergroten'}
        >
          {isExpanded ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );

  // Render timeline based on view mode
  const renderTimeline = () => {
    const data = timelineData;

    return (
      <div className="relative">
        {/* Current Period Display - Large and prominent */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 mb-3 border border-gray-200">
          <div className="text-center">
            <span className="text-2xl font-bold text-gray-700">
              {currentPeriod || (viewMode === 'year' ? goalStartDate.getFullYear().toString() : `${MONTH_NAMES[goalStartDate.getMonth()]} ${goalStartDate.getFullYear()}`)}
            </span>
          </div>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center justify-between gap-2 mb-2">
          {/* Period navigation (left) */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigateSegment('prev')}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              title={`Vorige ${viewMode === 'week' ? 'week' : viewMode === 'month' ? 'maand' : 'jaar'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-gray-500 px-2">
              {viewMode === 'week' ? 'Week' : viewMode === 'month' ? 'Maand' : 'Jaar'}
            </span>
            <button
              onClick={() => navigateSegment('next')}
              className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              title={`Volgende ${viewMode === 'week' ? 'week' : viewMode === 'month' ? 'maand' : 'jaar'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Milestone navigation (right) */}
          {milestonePositions.length > 0 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigateToMilestone('prev')}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors flex items-center gap-1"
                title="Vorige mijlpaal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-xs hidden sm:inline">Mijlpaal</span>
              </button>
              <span className="text-xs text-gray-600 px-1 font-medium inline-flex items-center gap-1">
                <GoalIcon size={14} className="text-gray-600" /> {milestonePositions.length}
              </span>
              <button
                onClick={() => navigateToMilestone('next')}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors flex items-center gap-1"
                title="Volgende mijlpaal"
              >
                <span className="text-xs hidden sm:inline">Mijlpaal</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Custom scrollbar styles */}
        <style>{`
          .timeline-scroll::-webkit-scrollbar {
            height: 8px;
          }
          .timeline-scroll::-webkit-scrollbar-track {
            background: #e5e7eb;
            border-radius: 4px;
          }
          .timeline-scroll::-webkit-scrollbar-thumb {
            background: #9ca3af;
            border-radius: 4px;
          }
        `}</style>

        {/* Scrollable container */}
        <div 
          ref={scrollContainerRef}
          className={`timeline-scroll pb-2 ${data.type === 'allYears' ? 'overflow-visible' : 'overflow-x-auto'}`}
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#f97316 #e5e7eb' }}
        >
          <div style={{ width: data.type === 'allYears' ? '100%' : `${data.totalWidth}px`, minWidth: '100%' }}>
            
            {/* YEAR VIEW - Langetermijn pad van jaren */}
            {data.type === 'year' && (
              <>
                {/* Info text - how many years */}
                <div className="text-center text-xs text-gray-500 mb-2">
                  {data.totalYears} {data.totalYears === 1 ? 'jaar' : 'jaren'} naar je doel
                </div>
                
                {/* Years row - large blocks representing each year */}
                <div className="flex justify-center">
                  {data.years.map((year, index) => (
                    <div
                      key={index}
                      className={`
                        flex-shrink-0 flex flex-col items-center justify-center
                        border-2 rounded-lg mx-1 transition-all
                        ${isExpanded ? 'h-24' : 'h-16'}
                        ${year.isCurrent 
                          ? 'bg-gray-100 border-gray-400 shadow-md' 
                          : year.isPast
                            ? 'bg-gray-50 border-gray-300'
                            : 'bg-gray-50 border-gray-200'
                        }
                        ${year.hasMilestones ? 'ring-2 ring-gray-300 ring-offset-1' : ''}
                      `}
                      style={{ width: `${YEAR_BLOCK_WIDTH}px` }}
                    >
                      {/* Year number */}
                      <span className={`font-bold ${isExpanded ? 'text-2xl' : 'text-xl'} ${
                        year.isCurrent 
                          ? 'text-gray-700' 
                          : year.isPast 
                            ? 'text-gray-600' 
                            : 'text-gray-700'
                      }`}>
                        {year.label}
                      </span>
                      
                      {/* Status indicator */}
                      <span className={`text-xs mt-1 inline-flex items-center gap-0.5 ${
                        year.isCurrent 
                          ? 'text-gray-600' 
                          : year.isPast 
                            ? 'text-gray-500' 
                            : 'text-gray-400'
                      }`}>
                        {year.isCurrent 
                          ? <><GoalIcon size={10} className="text-gray-600" /> Nu</>
                          : year.isPast 
                            ? '✓ Voorbij' 
                            : ''
                        }
                      </span>
                      
                      {/* Milestone indicator if this year has milestones */}
                      {year.hasMilestones && (
                        <div className="flex gap-1 mt-1">
                          {year.milestoneMonths.slice(0, 3).map(m => (
                            <span 
                              key={m} 
                              className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded"
                            >
                              {MONTH_NAMES[m]}
                            </span>
                          ))}
                          {year.milestoneMonths.length > 3 && (
                            <span className="text-[10px] text-gray-500">+{year.milestoneMonths.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Connection line between years */}
                <div className="flex justify-center items-center mt-2">
                  {data.years.map((year, index) => (
                    <React.Fragment key={year.year}>
                      {/* Year dot */}
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        year.isCurrent 
                          ? 'bg-gray-500 border-gray-600' 
                          : year.isPast 
                            ? 'bg-gray-400 border-gray-500' 
                            : 'bg-gray-200 border-gray-300'
                      }`} />
                      
                      {/* Connection line to next year */}
                      {index < data.years.length - 1 && (
                        <div className={`h-1 ${
                          year.isPast 
                            ? 'bg-gray-300' 
                            : 'bg-gray-200'
                        }`} style={{ width: `${YEAR_BLOCK_WIDTH - 16}px` }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            {/* MONTH VIEW */}
            {data.type === 'month' && (
              <>
                {/* Months row */}
                <div className="flex h-10">
                  {data.segments.map((segment, index) => (
                    <div
                      key={index}
                      className={`
                        flex-shrink-0 flex items-center justify-center border-r-2 border-gray-300
                        font-bold text-sm
                        ${segment.isCurrent 
                          ? 'bg-gray-300 text-gray-800' 
                          : 'bg-gray-200 text-gray-800'
                        }
                      `}
                      style={{ width: `${segment.pixelEnd - segment.pixelStart}px` }}
                    >
                      {segment.label}
                    </div>
                  ))}
                </div>
                
                {/* Days row (always visible) - with grayed out days */}
                <div className="flex h-8 border-t-2 border-gray-300">
                  {data.segments.flatMap((segment) => 
                    segment.days.map((day, dIndex) => {
                      const isWeekend = day.dayOfWeek === 0 || day.dayOfWeek === 6;
                      const isOutOfRange = day.isBeforeStart || day.isAfterEnd;
                      return (
                        <div
                          key={`${segment.year}-${segment.month}-${dIndex}`}
                          className={`
                            flex-shrink-0 flex items-center justify-center border-r border-gray-100
                            text-xs font-medium
                            ${isOutOfRange
                              ? 'bg-gray-100 text-gray-300'
                              : day.isCurrent 
                                ? 'bg-teal-100 text-teal-700 font-bold ring-2 ring-teal-400 ring-inset' 
                                : isWeekend
                                  ? 'bg-gray-200 text-gray-500'
                                  : 'bg-gray-50 text-gray-700'
                            }
                          `}
                          style={{ width: `${DAY_WIDTH}px` }}
                        >
                          {day.label}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {/* WEEK VIEW */}
            {data.type === 'week' && (
              <>
                {/* Weeks row */}
                <div className="flex h-10">
                  {data.segments.map((segment, index) => (
                    <div
                      key={index}
                      className={`
                        flex-shrink-0 flex items-center justify-center border-r-2 border-gray-300
                        font-bold text-sm
                        ${segment.isCurrent 
                          ? 'bg-gray-300 text-gray-800' 
                          : 'bg-gray-200 text-gray-800'
                        }
                      `}
                      style={{ width: `${segment.pixelEnd - segment.pixelStart}px` }}
                    >
                      {segment.label}
                    </div>
                  ))}
                </div>
                
                {/* Days row (always visible) - with grayed out days */}
                <div className="flex h-12 border-t-2 border-gray-300">
                  {data.segments.flatMap((segment) => 
                    segment.days.map((day, dIndex) => {
                      const isWeekend = day.dayOfWeek === 0 || day.dayOfWeek === 6;
                      const isOutOfRange = day.isBeforeStart || day.isAfterEnd;
                      return (
                        <div
                          key={`${segment.weekNum}-${dIndex}`}
                          className={`
                            flex-shrink-0 flex flex-col items-center justify-center border-r border-gray-100
                            ${isOutOfRange
                              ? 'bg-gray-100 text-gray-300'
                              : day.isCurrent 
                                ? 'bg-gray-200 text-gray-700 font-bold ring-2 ring-gray-400 ring-inset' 
                                : isWeekend
                                  ? 'bg-gray-200 text-gray-500'
                                  : 'bg-gray-50 text-gray-700'
                            }
                          `}
                          style={{ width: `${DAY_WIDTH}px` }}
                        >
                          <span className="text-[10px] font-semibold uppercase">{day.dayName}</span>
                          <span className="text-sm font-medium">{day.dayOfMonth}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {/* TIMELINE TRACK - only for week/month views */}
            {data.type !== 'year' && data.type !== 'allYears' && (
            <div className={`relative mt-3 ${isExpanded ? 'h-14' : 'h-10'}`}>
              {/* Track line */}
              <div 
                className={`absolute top-1/2 left-0 bg-gray-300 rounded-full -translate-y-1/2 ${isExpanded ? 'h-2' : 'h-1.5'}`}
                style={{ width: `${data.totalWidth}px` }}
              />

              {/* Video slot markers (squares) - every 7 days */}
              {videoSlotPositions.map((slot, index) => {
                const daysUntilOpen = Math.ceil((slot.date.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
                const isLocked = !slot.isRecorded && !slot.isAvailable && daysUntilOpen > 0;
                
                return (
                  <button
                    key={`video-${slot.dayNumber}`}
                    onClick={() => handleVideoSlotClick(slot)}
                    disabled={isLocked}
                    className={`
                      absolute top-1/2 -translate-y-1/2 -translate-x-1/2
                      rounded-sm border-2 transition-all flex items-center justify-center
                      ${!isLocked ? 'hover:scale-125 hover:z-20 cursor-pointer' : 'cursor-not-allowed'}
                      ${isExpanded ? 'w-5 h-5' : 'w-4 h-4'}
                      ${slot.isRecorded 
                        ? 'bg-gray-500 border-gray-600' 
                        : slot.isAvailable
                          ? 'bg-white border-gray-400 hover:border-gray-500'
                          : 'bg-gray-200 border-gray-300'
                      }
                    `}
                    style={{ left: `${slot.pixelPosition}px` }}
                    title={
                      slot.isRecorded 
                        ? `Video ${slot.dayNumber === 0 ? 'Start' : `Dag ${slot.dayNumber}`} - Klik om te bekijken`
                        : slot.isAvailable
                          ? `Video ${slot.dayNumber === 0 ? 'Start' : `Dag ${slot.dayNumber}`} - Klik om op te nemen`
                          : `Nog ${daysUntilOpen} dag${daysUntilOpen !== 1 ? 'en' : ''}`
                    }
                  >
                    {slot.isRecorded ? (
                      <span className={`text-white ${isExpanded ? 'text-[10px]' : 'text-[8px]'}`}>▶</span>
                    ) : slot.isAvailable ? (
                      <span className={`text-gray-500 ${isExpanded ? 'text-[10px]' : 'text-[8px]'}`}>●</span>
                    ) : null}
                  </button>
                );
              })}

              {/* Today marker - only show if today is within goal range */}
              {today >= goalStartDate && today <= goalEndDate && todayPixelPosition >= 0 && todayPixelPosition <= data.totalWidth && (
                <div
                  className={`absolute top-0 bottom-0 bg-gray-500 z-10 ${isExpanded ? 'w-1' : 'w-0.5'}`}
                  style={{ left: `${todayPixelPosition}px` }}
                />
              )}

              {/* Start marker - circle with checkmark BEFORE video slot */}
              {startPixelPosition > 0 && startPixelPosition <= data.totalWidth && (
                <div
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 flex items-center justify-center z-5
                    bg-gray-400 border-gray-500 ${isExpanded ? 'w-6 h-6' : 'w-5 h-5'}`}
                  style={{ left: `${startPixelPosition - (isExpanded ? 20 : 15)}px` }}
                  title="Startdatum doel"
                >
                  <span className={`text-white ${isExpanded ? 'text-xs' : 'text-[10px]'}`}>✓</span>
                </div>
              )}

              {/* End marker - flag at the end of goal */}
              {endPixelPosition > 0 && endPixelPosition <= data.totalWidth && (
                <div
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 flex items-center justify-center
                    bg-gray-600 border-gray-700 ${isExpanded ? 'w-6 h-6' : 'w-5 h-5'}`}
                  style={{ left: `${endPixelPosition}px` }}
                  title="Einddatum"
                >
                  <MilestoneIcon size={isExpanded ? 12 : 10} className="text-white" />
                </div>
              )}

              {/* Milestone markers (circles) - with chronological numbers and logic warnings */}
              {milestonePositions
                .filter(({ hasDate, pixelPosition }) => hasDate || pixelPosition > 10)
                .map(({ milestone, pixelPosition, isPast, hasDate, date }, index, filteredArray) => {
                  const chronologicalNumber = index + 1;
                  const totalMilestones = filteredArray.length;
                  
                  // Check if milestone might be in wrong position based on content
                  const titleLower = milestone.title.toLowerCase();
                  const isLearningMilestone = ['leren', 'cursus', 'training', 'studeren', 'onderzoeken', 'kennis'].some(kw => titleLower.includes(kw));
                  const isGrowthMilestone = ['groeien', 'schalen', 'uitbreiden', 'team', 'aannemen'].some(kw => titleLower.includes(kw));
                  
                  // Learning should be early (first 30%), growth should be late (last 40%)
                  const positionPercent = chronologicalNumber / totalMilestones;
                  const mightBeWrongPosition = 
                    (isLearningMilestone && positionPercent > 0.5) || // Learning in second half
                    (isGrowthMilestone && positionPercent < 0.4);     // Growth in first 40%
                  
                  return (
                    <button
                      key={milestone.id}
                      onClick={() => handleMilestoneClick(milestone)}
                      className={`
                        absolute top-1/2 -translate-y-1/2 -translate-x-1/2
                        rounded-full border-2 transition-all
                        hover:scale-125 hover:z-20 cursor-pointer
                        flex items-center justify-center
                        ${isExpanded ? 'w-6 h-6' : 'w-5 h-5'}
                        ${mightBeWrongPosition
                          ? 'bg-gray-100 border-gray-400 text-gray-700 ring-2 ring-gray-300'
                          : isPast 
                            ? 'bg-gray-400 border-gray-500 text-white' 
                            : hasDate
                              ? 'bg-white border-gray-400 hover:border-gray-500 text-gray-600'
                              : 'bg-gray-100 border-gray-300 hover:border-gray-400 border-dashed text-gray-400'
                        }
                      `}
                      style={{ left: `${pixelPosition}px` }}
                      title={`Mijlpaal ${chronologicalNumber}: ${milestone.title}${date ? `\n${formatDate(date)}` : ' (geen datum)'}${mightBeWrongPosition ? '\nMogelijk onlogische positie - klik om aan te passen' : ''}`}
                    >
                      <span className={`font-bold ${isExpanded ? 'text-[10px]' : 'text-[8px]'}`}>
                        {mightBeWrongPosition ? '!' : chronologicalNumber}
                      </span>
                    </button>
                  );
                })}
            </div>
            )}

            {/* LABELS ROW - vaste hoogte, geen overflow - only for week/month views */}
            {data.type !== 'year' && data.type !== 'allYears' && (
            <div className={`relative ${isExpanded ? 'h-28' : 'h-20'} mt-2`}>
              {/* Video slot labels */}
              {videoSlotPositions.slice(0, 5).map((slot, index) => (
                <div 
                  key={`video-label-${slot.dayNumber}`}
                  className={`absolute whitespace-nowrap px-1.5 py-0.5 rounded ${isExpanded ? 'text-xs' : 'text-[10px]'} ${
                    slot.isRecorded 
                      ? 'bg-gray-500 text-white' 
                      : slot.isAvailable
                        ? 'bg-gray-100 text-gray-700 border border-gray-300'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                  style={{ 
                    left: `${slot.pixelPosition}px`, 
                    transform: 'translateX(-50%)', 
                    top: index % 2 === 0 ? 0 : (isExpanded ? '18px' : '14px')
                  }}
                >
                  {slot.dayNumber === 0 ? <GrowthIcon size={12} className="inline" /> : <VideoIcon size={12} className="inline" />} {slot.dayNumber === 0 ? 'Start' : `D${slot.dayNumber}`}
                </div>
              ))}
              
              {/* Today label - only show if today is within goal range */}
              {today >= goalStartDate && today <= goalEndDate && todayPixelPosition >= 0 && todayPixelPosition <= data.totalWidth && (
                <div 
                  className={`absolute bg-gray-500 text-white font-medium whitespace-nowrap px-2 py-0.5 rounded ${isExpanded ? 'text-xs' : 'text-[10px]'}`}
                  style={{ 
                    left: `${todayPixelPosition}px`, 
                    transform: 'translateX(-50%)', 
                    top: 0 
                  }}
                >
                  <GoalIcon size={12} className="inline" /> Vandaag
                </div>
              )}

              {/* End label - shows goal end date */}
              {endPixelPosition > 0 && endPixelPosition <= data.totalWidth && (
                <div 
                  className={`absolute bg-gray-600 text-white font-medium whitespace-nowrap px-2 py-0.5 rounded ${isExpanded ? 'text-xs' : 'text-[10px]'}`}
                  style={{ 
                    left: `${endPixelPosition}px`, 
                    transform: 'translateX(-50%)', 
                    top: isExpanded ? '18px' : '14px'
                  }}
                >
                  <MilestoneIcon size={12} className="inline" /> Einde
                </div>
              )}

              {/* Milestone labels - styled cards for better visibility */}
              {/* Numbers are CHRONOLOGICAL + warnings for illogical positions with category tooltips */}
              {milestonePositions
                .filter(({ hasDate, pixelPosition }) => hasDate || pixelPosition > 10)
                .map(({ milestone, pixelPosition, date }, index, filteredArray) => {
                  const chronologicalNumber = index + 1;
                  const totalMilestones = filteredArray.length;
                  
                  // Detect milestone category
                  const titleLower = milestone.title.toLowerCase();
                  type MilestoneCategory = 'learn' | 'plan' | 'build' | 'grow' | 'goal' | 'unknown';
                  let category: MilestoneCategory = 'unknown';
                  let categoryInfo: { icon: React.ReactNode; label: string; description: string; idealPosition: string } = { icon: '📌', label: 'Algemeen', description: 'Een mijlpaal op weg naar je doel.', idealPosition: 'Afhankelijk van de inhoud' };
                  
                  if (['leren', 'cursus', 'training', 'studeren', 'onderzoeken', 'kennis', 'certificaat', 'opleiding', 'workshop', 'boek'].some(kw => titleLower.includes(kw))) {
                    category = 'learn';
                    categoryInfo = { icon: '🎓', label: 'Leerdoel', description: 'Kennis opdoen, cursussen volgen, onderzoeken. Dit is de basis.', idealPosition: 'Begin van traject (eerste 20%)' };
                  } else if (['plan', 'strategie', 'opzetten', 'voorbereiden', 'ontwerpen', 'bepalen', 'roadmap', 'concept'].some(kw => titleLower.includes(kw))) {
                    category = 'plan';
                    categoryInfo = { icon: <ClipboardIcon size={14} />, label: 'Planningsdoel', description: 'Strategie uitwerken, plannen maken. De blauwdruk voor actie.', idealPosition: 'Vroeg in traject (15-35%)' };
                  } else if (['bouwen', 'maken', 'lanceren', 'starten', 'creëren', 'ontwikkelen', 'implementeren', 'verkopen', 'werven'].some(kw => titleLower.includes(kw))) {
                    category = 'build';
                    categoryInfo = { icon: '🔨', label: 'Uitvoeringsdoel', description: 'Bouwen, maken, lanceren. Actie ondernemen.', idealPosition: 'Midden van traject (30-60%)' };
                  } else if (['groeien', 'uitbreiden', 'schalen', 'verdubbelen', 'optimaliseren', 'team', 'aannemen', 'delegeren', 'automatiseren'].some(kw => titleLower.includes(kw))) {
                    category = 'grow';
                    categoryInfo = { icon: <TrendIcon size={14} />, label: 'Groeidoel', description: 'Uitbreiden, schalen, optimaliseren. Groei ontstaat wanneer de fundering staat.', idealPosition: 'Tweede helft traject (55-90%)' };
                  } else if (['einddoel', 'ultiem', 'bereiken', 'target', 'visie', 'missie', 'succes'].some(kw => titleLower.includes(kw))) {
                    category = 'goal';
                    categoryInfo = { icon: <GoalIcon size={14} />, label: 'Einddoel', description: 'Het ultieme resultaat waar alles naartoe werkt.', idealPosition: 'Einde van traject (95-100%)' };
                  }
                  
                  // Check if position is logical
                  const positionPercent = chronologicalNumber / totalMilestones;
                  const idealRanges: Record<MilestoneCategory, { min: number; max: number }> = {
                    learn: { min: 0, max: 0.35 },
                    plan: { min: 0.05, max: 0.45 },
                    build: { min: 0.20, max: 0.75 },
                    grow: { min: 0.40, max: 0.95 },
                    goal: { min: 0.80, max: 1.0 },
                    unknown: { min: 0, max: 1.0 },
                  };
                  const range = idealRanges[category];
                  const mightBeWrongPosition = positionPercent < range.min || positionPercent > range.max;
                  
                  // Build comprehensive tooltip
                  const tooltipLines = [
                    categoryInfo.label,
                    '',
                    milestone.title,
                    date ? formatDate(date) : 'Geen datum',
                    '',
                    categoryInfo.description,
                    `📍 Ideale positie: ${categoryInfo.idealPosition}`,
                    `Huidige positie: ${Math.round(positionPercent * 100)}%`,
                  ];
                  
                  if (mightBeWrongPosition) {
                    tooltipLines.push('');
                    tooltipLines.push('WAARSCHUWING:');
                    if (category === 'learn' && positionPercent > range.max) {
                      tooltipLines.push('Dit leerdoel staat laat in het traject.');
                      tooltipLines.push('Tip: Leren hoort meestal eerder, zodat je de kennis kunt toepassen.');
                    } else if (category === 'grow' && positionPercent < range.min) {
                      tooltipLines.push('Dit groeidoel staat vroeg in het traject.');
                      tooltipLines.push('Tip: Groei komt pas wanneer de fundering staat.');
                    } else {
                      tooltipLines.push('Dit doel lijkt niet op de ideale positie te staan.');
                      tooltipLines.push('Klik om aan te passen.');
                    }
                  } else {
                    tooltipLines.push('');
                    tooltipLines.push('✓ Positie lijkt logisch');
                  }
                  
                  tooltipLines.push('');
                  tooltipLines.push('Klik voor details en bewerken');
                  
                  return (
                    <button
                      key={milestone.id}
                      onClick={() => handleMilestoneClick(milestone)}
                      className={`
                        absolute cursor-pointer transition-all
                        rounded-lg shadow-sm
                        hover:shadow-md hover:z-30
                        px-2 py-1
                        ${isExpanded ? 'text-xs' : 'text-[11px]'}
                        ${mightBeWrongPosition 
                          ? 'bg-gray-50 border-2 border-gray-400 hover:bg-gray-100' 
                          : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-400'
                        }
                      `}
                      style={{ 
                        left: `${pixelPosition}px`,
                        transform: 'translateX(-50%)',
                        top: index % 2 === 0 
                          ? (isExpanded ? '4px' : '2px')
                          : (isExpanded ? '36px' : '28px'),
                        maxWidth: isExpanded ? '180px' : '140px',
                      }}
                      title={tooltipLines.join('\n')}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full font-bold text-[10px] flex items-center justify-center
                          ${mightBeWrongPosition 
                            ? 'bg-gray-200 text-gray-700' 
                            : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {mightBeWrongPosition ? categoryInfo.icon : chronologicalNumber}
                        </span>
                        <span className={`font-medium truncate ${mightBeWrongPosition ? 'text-gray-700' : 'text-gray-700'}`}>
                          {milestone.globalPeriod || truncateText(milestone.title, isExpanded ? 20 : 12)}
                        </span>
                      </div>
                      {isExpanded && (
                        <div className={`text-[10px] truncate mt-0.5 ${mightBeWrongPosition ? 'text-gray-600' : 'text-gray-500'}`}>
                          {categoryInfo.icon} {categoryInfo.label} • {truncateText(milestone.title, 20)}
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
            )}

            {/* YEAR VIEW: Milestone list below the year blocks */}
            {data.type === 'year' && milestones.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-2">Mijlpalen in dit traject:</div>
                <div className="flex flex-wrap gap-2">
                  {milestonesWithDates.map(({ milestone, date }) => (
                    <button
                      key={milestone.id}
                      onClick={() => handleMilestoneClick(milestone)}
                      className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <span className="w-2 h-2 rounded-full bg-gray-400" />
                      <span className="font-medium">{date ? `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}` : 'Geen datum'}</span>
                      <span className="text-gray-600">- {truncateText(milestone.title, 25)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* ALL YEARS VIEW: Stacked years with monthly timeline and drag-drop */}
            {data.type === 'allYears' && (
              <div className="space-y-2">
                {/* Header */}
                <div className="text-center text-sm text-gray-600 mb-4">
                  <span className="font-medium">{data.totalYears} jaren</span> naar je doel
                  <span className="text-xs text-gray-400 ml-2">• Sleep mijlpalen om te verplaatsen</span>
                </div>
                
                {/* Year rows */}
                {data.yearRows.map((yearRow) => (
                  <div 
                    key={yearRow.year}
                    className={`
                      flex items-stretch rounded-lg border-2 overflow-hidden
                      ${yearRow.isCurrent 
                        ? 'border-gray-400 bg-gray-50/30' 
                        : yearRow.isPast 
                          ? 'border-gray-300 bg-gray-50/30'
                          : 'border-gray-200 bg-gray-50/30'
                      }
                    `}
                  >
                    {/* Year label */}
                    <div className={`
                      w-20 flex-shrink-0 flex flex-col items-center justify-center py-3 px-2
                      ${yearRow.isCurrent 
                        ? 'bg-gray-100' 
                        : yearRow.isPast 
                          ? 'bg-gray-100'
                          : 'bg-gray-100'
                      }
                    `}>
                      <span className={`font-bold text-xl ${
                        yearRow.isCurrent 
                          ? 'text-gray-700' 
                          : yearRow.isPast 
                            ? 'text-gray-600'
                            : 'text-gray-600'
                      }`}>
                        {yearRow.year}
                      </span>
                      {yearRow.isCurrent && (
                        <span className="text-[10px] text-gray-600 mt-0.5 inline-flex items-center gap-0.5"><GoalIcon size={10} className="text-gray-600" /> Nu</span>
                      )}
                    </div>
                    
                    {/* Months grid */}
                    <div className="flex-1 flex">
                      {yearRow.months.map((monthData) => (
                        <div
                          key={`${yearRow.year}-${monthData.month}`}
                          className={`
                            flex-1 min-w-0 border-l first:border-l-0 transition-all relative
                            ${dropTarget?.year === yearRow.year && dropTarget?.month === monthData.month
                              ? 'bg-gray-200 ring-2 ring-gray-400 ring-inset'
                              : monthData.isCurrent
                                ? 'bg-gray-100/50'
                                : monthData.isPast
                                  ? 'bg-gray-50/50'
                                  : 'bg-white/50'
                            }
                            ${monthData.hasMilestones ? 'bg-gray-50' : ''}
                          `}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDropTarget({ year: yearRow.year, month: monthData.month });
                          }}
                          onDragLeave={() => setDropTarget(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDropTarget(null);
                            if (draggedMilestone) {
                              setShowDatePickerModal({
                                milestone: draggedMilestone,
                                targetYear: yearRow.year,
                                targetMonth: monthData.month,
                              });
                              setDraggedMilestone(null);
                            }
                          }}
                        >
                          {/* Month label */}
                          <div className={`text-center py-1 text-[10px] font-medium border-b
                            ${monthData.isCurrent 
                              ? 'text-gray-700 bg-gray-100/50' 
                              : monthData.isPast
                                ? 'text-gray-600 bg-gray-50/50'
                                : 'text-gray-500 bg-gray-50/50'
                            }
                          `}>
                            {monthData.label}
                          </div>
                          
                          {/* Milestone indicators */}
                          <div className="p-1 min-h-[40px] flex flex-col gap-0.5">
                            {monthData.milestonesInMonth.map(({ milestone }) => (
                              <div
                                key={milestone.id}
                                draggable
                                onDragStart={(e) => {
                                  setDraggedMilestone(milestone);
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={() => {
                                  setDraggedMilestone(null);
                                  setDropTarget(null);
                                }}
                                onClick={() => handleMilestoneClick(milestone)}
                                className={`
                                  cursor-move px-1 py-0.5 rounded text-[9px] leading-tight
                                  truncate transition-all
                                  ${draggedMilestone?.id === milestone.id
                                    ? 'opacity-50 bg-gray-300'
                                    : 'bg-gray-400 text-white hover:bg-gray-500'
                                  }
                                `}
                                title={`${milestone.title}\n\nSleep naar een andere maand om te verplaatsen\nof klik om te bewerken`}
                              >
                                {truncateText(milestone.title, 8)}
                              </div>
                            ))}
                            
                            {/* Milestone count badge if many */}
                            {monthData.milestoneCount > 2 && (
                              <div className="text-[8px] text-gray-600 text-center">
                                +{monthData.milestoneCount - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Unscheduled milestones */}
                {milestones.filter(m => !m.targetDate).length > 0 && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                    <div className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                      <CalendarIcon size={14} className="text-gray-600" /> Niet-ingeplande mijlpalen ({milestones.filter(m => !m.targetDate).length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {milestones.filter(m => !m.targetDate).map(milestone => (
                        <div
                          key={milestone.id}
                          draggable
                          onDragStart={(e) => {
                            setDraggedMilestone(milestone);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDraggedMilestone(null);
                            setDropTarget(null);
                          }}
                          onClick={() => handleMilestoneClick(milestone)}
                          className={`
                            cursor-move px-2 py-1 rounded text-xs
                            ${draggedMilestone?.id === milestone.id
                              ? 'opacity-50 bg-gray-400'
                              : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                            }
                          `}
                          title="Sleep naar een maand om in te plannen"
                        >
                          {truncateText(milestone.title, 20)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Legend & Info - compact with hover tooltip */}
        <div className={`flex items-center justify-between mt-4 ${isExpanded ? 'text-sm' : 'text-xs'}`}>
          {/* Info text */}
          <div className="text-gray-500">
            {milestones.length > 0 
              ? `${milestones.length} mijlpaal${milestones.length !== 1 ? 'en' : ''}`
              : 'Geen mijlpalen'
            }
            {videoSlotPositions.filter(s => s.isRecorded).length > 0 && (
              <span> • {videoSlotPositions.filter(s => s.isRecorded).length} video{videoSlotPositions.filter(s => s.isRecorded).length !== 1 ? "'s" : ''}</span>
            )}
          </div>
          
          {/* Legend hover button */}
          <div className="relative group">
            <button className="flex items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors px-2 py-1 rounded hover:bg-gray-100">
              <InfoIcon size={14} className="text-gray-400" />
              <span>Legenda</span>
            </button>
            
            {/* Tooltip - appears on hover */}
            <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
                <div className="text-xs font-medium text-gray-700 mb-2">Video markers</div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-sm bg-gray-500 border-2 border-gray-600 flex items-center justify-center">
                      <span className="text-white text-[8px]">▶</span>
                    </div>
                    <span>Video opgenomen</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-sm bg-white border-2 border-gray-400 flex items-center justify-center">
                      <span className="text-gray-500 text-[8px]">●</span>
                    </div>
                    <span>Video slot open</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-sm bg-gray-200 border-2 border-gray-300" />
                    <span>Video slot gesloten</span>
                  </div>
                </div>
                
                <div className="text-xs font-medium text-gray-700 mb-2">Mijlpaal markers</div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-full bg-gray-400 border-2 border-gray-500 flex items-center justify-center">
                      <span className="text-white text-[8px]">✓</span>
                    </div>
                    <span>Start</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-400" />
                    <span>Mijlpaal</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-full bg-gray-100 border-2 border-gray-300 border-dashed" />
                    <span>Mijlpaal (geen datum)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-full bg-gray-400 border-2 border-gray-500" />
                    <span>Mijlpaal voorbij</span>
                  </div>
                </div>
                
                <div className="text-xs font-medium text-gray-700 mb-2">Overig</div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-1.5 bg-gray-500 rounded-sm" />
                    <span>Vandaag</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-4 h-4 rounded-full bg-gray-600 border-2 border-gray-700 flex items-center justify-center">
                      <MilestoneIcon size={8} className="text-white" />
                    </div>
                    <span>Einde</span>
                  </div>
                </div>
                
                {/* Arrow pointing down */}
                <div className="absolute -bottom-1 right-4 w-2 h-2 bg-white border-r border-b border-gray-200 transform rotate-45" />
              </div>
            </div>
          </div>
        </div>

        {/* Empty state */}
        {milestones.length === 0 && (
          <div className={`text-center py-4 text-gray-400 mt-2 border-t border-gray-100 ${isExpanded ? 'text-base' : 'text-sm'}`}>
            Nog geen mijlpalen. Start een sparringsessie om je mijlpalen te ontdekken.
          </div>
        )}

        {/* Video Slot Modal */}
        {selectedVideoSlot && (
          <VideoSlotModal
            slot={selectedVideoSlot}
            goal={goal}
            goalId={goal.id}
            ownerId={ownerId}
            onSave={handleSaveReflection}
            onDelete={selectedVideoSlot.entry ? () => handleDeleteEntry(selectedVideoSlot.entry!.id) : undefined}
            onClose={() => setSelectedVideoSlot(null)}
          />
        )}

        {/* Edit Modal */}
        {selectedMilestone && (
          <MilestoneEditModal
            milestone={selectedMilestone}
            goalId={goal.id}
            ownerId={ownerId}
            goalStartDate={goalStartDate}
            goalEndDate={goalEndDate}
            onSave={handleMilestoneSave}
            onClose={() => setSelectedMilestone(null)}
          />
        )}
        
        {/* Date Picker Modal for drag-drop in allYears view */}
        {showDatePickerModal && (
          <DatePickerModal
            milestone={showDatePickerModal.milestone}
            targetYear={showDatePickerModal.targetYear}
            targetMonth={showDatePickerModal.targetMonth}
            goalStartDate={goalStartDate}
            goalEndDate={goalEndDate}
            onSave={async (newDate) => {
              // Save scroll position before update
              let savedScrollTop = 0;
              let savedScrollLeft = 0;
              
              // Find the actual scrollable container and save its position
              if (isExpanded) {
                // Expanded view: scroll is on the outer fixed container
                if (expandedScrollContainerRef.current) {
                  savedScrollTop = expandedScrollContainerRef.current.scrollTop;
                }
              } else {
                // Normal view: use scrollContainerRef for horizontal scroll
                if (scrollContainerRef.current) {
                  savedScrollTop = scrollContainerRef.current.scrollTop;
                  savedScrollLeft = scrollContainerRef.current.scrollLeft;
                }
                // For allYears view, also save window scroll (vertical)
                if (viewMode === 'allYears') {
                  savedScrollTop = window.scrollY;
                }
              }
              
              try {
                await updateMilestone(showDatePickerModal.milestone.id, { 
                  targetDate: newDate.toISOString().split('T')[0] 
                });
                const data = await getMilestonesByGoal(ownerId, goal.id);
                setLoadedMilestones(data);
                if (onMilestoneUpdated) onMilestoneUpdated();
                
                // Restore scroll position after update
                // Use requestAnimationFrame + setTimeout to ensure DOM has fully updated
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    if (isExpanded) {
                      // Expanded view: restore scroll on outer container
                      if (expandedScrollContainerRef.current) {
                        expandedScrollContainerRef.current.scrollTop = savedScrollTop;
                      }
                    } else {
                      // Normal view: restore horizontal scroll
                      if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = savedScrollTop;
                        scrollContainerRef.current.scrollLeft = savedScrollLeft;
                      }
                      // For allYears view, also restore window scroll (vertical)
                      if (viewMode === 'allYears') {
                        window.scrollTo({ top: savedScrollTop, behavior: 'auto' });
                      }
                    }
                  }, 150);
                });
              } catch (err) {
                console.error('Failed to update milestone date:', err);
              }
              setShowDatePickerModal(null);
            }}
            onClose={() => setShowDatePickerModal(null)}
          />
        )}
      </div>
    );
  };

  // Expanded fullscreen view
  if (isExpanded) {
    return (
      <div 
        ref={expandedScrollContainerRef}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-auto"
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] max-h-[95dvh] overflow-auto p-6">
          {timelineHeader}
          {renderTimeline()}
        </div>
      </div>
    );
  }

  // Normal view
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {timelineHeader}
      {renderTimeline()}
    </div>
  );
};

/**
 * Calculate date for milestone based on globalPeriod or targetDate
 */
function calculateMilestoneDate(
  milestone: Milestone,
  goalStart: Date,
  goalEnd: Date
): Date | null {
  if (milestone.targetDate && milestone.targetDate.length > 0) {
    return new Date(milestone.targetDate);
  }

  const period = milestone.globalPeriod?.toLowerCase() || '';
  const today = new Date();

  if (period.includes('deze week')) {
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek;
  }
  if (period.includes('3 maanden') || period.includes('drie maanden')) {
    const date = new Date(today);
    date.setMonth(date.getMonth() + 3);
    return date;
  }
  if (period.includes('6 maanden') || period.includes('half jaar')) {
    const date = new Date(today);
    date.setMonth(date.getMonth() + 6);
    return date;
  }
  if (period.includes('1 jaar') || period.includes('over 1 jaar')) {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() + 1);
    return date;
  }

  if (period.includes('q1')) return new Date(extractYear(period) || today.getFullYear(), 1, 15);
  if (period.includes('q2')) return new Date(extractYear(period) || today.getFullYear(), 4, 15);
  if (period.includes('q3')) return new Date(extractYear(period) || today.getFullYear(), 7, 15);
  if (period.includes('q4')) return new Date(extractYear(period) || today.getFullYear(), 10, 15);

  const monthMap: Record<string, number> = {
    'januari': 0, 'februari': 1, 'maart': 2, 'april': 3, 'mei': 4, 'juni': 5,
    'juli': 6, 'augustus': 7, 'september': 8, 'oktober': 9, 'november': 10, 'december': 11,
  };

  for (const [monthName, monthIndex] of Object.entries(monthMap)) {
    if (period.includes(monthName)) {
      return new Date(extractYear(period) || today.getFullYear(), monthIndex, 15);
    }
  }

  return null;
}

function extractYear(text: string): number | null {
  const match = text.match(/\b(20\d{2})\b/);
  return match ? parseInt(match[1]) : null;
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
}

export default GoalTimeline;
